import { Component, Input, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { DirectPlan } from '@app/modules/direct/models/direct-subscription.interface';
import { DirectSubscriptionService } from '@app/modules/direct/services/direct-subscription.service';
import {
  CancellationPolicy,
  DirectService,
  PropertySite,
  SitePublishStatuses,
} from '@app/modules/direct/services/direct.service';
import {
  PayoutMethod,
  PayoutMethodProvider,
  PayoutMethodsService,
} from '@app/modules/direct/services/payout-methods.service';
import { NotificationType } from '@app/shared/interfaces';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { ClipboardService } from 'ngx-clipboard';
import { map, tap } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'sbnb-property-detail-direct',
  templateUrl: './sbnb-property-detail-direct.component.html',
  styleUrls: ['./sbnb-property-detail-direct.component.scss'],
})
export class PropertyDetailDirectComponent implements OnInit {
  SitePublishStatuses = SitePublishStatuses;

  isDirectOnboarded$ = this.directSub.isOnboarded$;
  isPremium$ = this.directSub.isPremium$;
  isBasic$ = this.directSub.isBasic$;

  @Input() propertyId: number;
  @Input() propertySlug: string;

  loadingSites: boolean = false;
  propertySites: PropertySite[] = [];

  loadingPromoCodes: boolean = false;
  promoCodes: string[] = [];

  loadingTaxes: boolean = false;
  taxes: any[] = [];

  loadingPayoutMethods: boolean = false;
  payoutMethods: PayoutMethod[] = [];
  activePayoutMethod: PayoutMethod | null = null;
  savingPayoutMethod: boolean = false;
  recentlySavedPayoutMethod: boolean = false;

  loadingCancellationPolicies: boolean = false;
  cancellationPolicies: CancellationPolicy[] = [];
  activeCancellationPolicy: CancellationPolicy | null = null;
  savingCancellationPolicy: boolean = false;
  recentlysavedCancellationPolicy: boolean = false;

  constructor(
    private directSub: DirectSubscriptionService,
    private direct: DirectService,
    private clipboardService: ClipboardService,
    private toast: ToastNotificationsService,
    private dialog: MatDialog,
    private payoutMethodsService: PayoutMethodsService
  ) { }

  ngOnInit(): void {
    this.directSub.isOnboarded$
      .pipe(
        tap((isOnboarded) => {
          if (isOnboarded) {
            this.fetchPropertySites(this.propertyId);
            this.fetchPromoCodes(this.propertyId);
            this.fetchPayoutMethods(this.propertyId, this.directSub.getCurrentSubscriptionStatus().plan);
            this.fetchCancellationPolicies(this.propertyId);
          }
        })
      )
      .subscribe();

    this.directSub.isPremium$
      .pipe(
        tap((isPremium) => {
          if (isPremium) {
            this.fetchTaxes(this.propertyId);
          }
        })
      )
      .subscribe();
  }

  fetchPropertySites(propertyId: number) {
    this.loadingSites = true;

    this.direct.getPropertySites(propertyId).subscribe(propertySites => {
      this.propertySites = propertySites;
      this.loadingSites = false;
    });
  }

  fetchPromoCodes(propertyId: number) {
    this.loadingPromoCodes = true;

    this.direct
      .getPromoCodes()
      .pipe(
        map((codes) => {
          return codes
            .filter((code) => code.properties.includes(propertyId) && code.enabled)
            .reduce((acc, curr) => acc.concat(curr.codes), []);
        }),
        tap(() => (this.loadingPromoCodes = false))
      )
      .subscribe(
        (res) => (this.promoCodes = res),
        (err) => {
          console.error('Error fetching promo codes:', err);
        }
      );
  }

  fetchPayoutMethods(propertyId: number, plan: DirectPlan) {
    this.loadingPayoutMethods = true;

    this.payoutMethodsService.fetchPayoutMethods().subscribe(
      (res) => {
        this.loadingPayoutMethods = false;
        this.payoutMethods = res.filter(
          (method) =>
            method.provider ===
            (plan === DirectPlan.Premium ? PayoutMethodProvider.ADYEN : PayoutMethodProvider.STRIPE_OAUTH)
        );

        if (this.payoutMethods?.length > 0) {
          this.activePayoutMethod = this.payoutMethods.find(
            (payoutMethod) => payoutMethod.property_scope.indexOf(propertyId) > -1
          );

          if (!this.activePayoutMethod) {
            this.activePayoutMethod = this.payoutMethods
              .filter(
                (method) =>
                  method.provider ===
                  (plan === DirectPlan.Premium ? PayoutMethodProvider.ADYEN : PayoutMethodProvider.STRIPE_OAUTH)
              )
              .find((method) => {
                return method.all_properties === true;
              });
          }
        }
      },
      (err) => {
        console.error('Error fetching payout methods:', err);
        this.loadingPayoutMethods = false;
      }
    );
  }

  updatePayoutMethod(propertyId: number, paymentMethodUuid: PayoutMethod['uuid']) {
    this.savingPayoutMethod = true;

    this.payoutMethodsService.setPayoutMethodForProperty(propertyId, paymentMethodUuid).subscribe(
      (res) => {
        this.savingPayoutMethod = false;

        this.recentlySavedPayoutMethod = true;
        setTimeout(() => {
          this.recentlySavedPayoutMethod = false;
        }, 3000);
      },
      (err) => {
        console.error('Error updating payout method:', err);
        this.savingPayoutMethod = false;
      }
    );
  }

  fetchCancellationPolicies(propertyId: number) {
    this.loadingCancellationPolicies = true;

    this.direct.fetchCancellationPolicies().subscribe(
      (res) => {
        this.loadingCancellationPolicies = false;
        this.cancellationPolicies = res;

        this.activeCancellationPolicy = this.cancellationPolicies.find(
          (policy) => policy.properties.indexOf(propertyId) > -1
        );

        if (!this.activeCancellationPolicy) {
          this.activeCancellationPolicy = this.cancellationPolicies.find((policy) => policy.default === true);
        }
      },
      (err) => {
        console.error('Error fetching cancellation policies:', err);
        this.loadingCancellationPolicies = false;
      }
    );
  }

  updateCancellationPolicy(propertyId: number, policyUuid: CancellationPolicy['uuid']) {
    return;
    // this.savingCancellationPolicy = true;

    // this.direct.todo.subscribe(
    //   (res) => {
    //     this.savingPayoutMethod = false;

    //     this.recentlySavedPayoutMethod = true;
    //     setTimeout(() => {
    //       this.recentlySavedPayoutMethod = false;
    //     }, 3000);
    //   },
    //   (err) => {
    //     console.error('Error updating payout method:', err);
    //     this.savingPayoutMethod = false;
    //   }
    // );
  }

  copyPropertyURL(site: PropertySite) {
    this.clipboardService.copy(site.url);
    this.toast.open('Copied to clipboard', 'Dismiss', NotificationType.Success, 2500);
  }

  copyPromoCode(code: string) {
    this.clipboardService.copy(code);
    this.toast.open('Copied to clipboard', 'Dismiss', NotificationType.Success, 2500);
  }

  fetchTaxes(propertyId: number) {
    this.loadingTaxes = true;

    this.direct.fetchPremiumTaxEstimates(this.propertyId).subscribe(
      (res) => {
        this.taxes = this.transformTaxData(res);
        this.loadingTaxes = false;
      },
      (err) => {
        console.error('Error fetching taxes:', err);
        this.loadingTaxes = false;
      }
    );
  }

  transformTaxData(taxes: any[]): any {
    const groupedData = {};

    taxes.forEach((entry) => {
      const key = `${entry.jurisdiction.name} ${entry.tax.name} ${entry.tax.rate.formatted_decimal}`;

      if (!groupedData[key]) {
        groupedData[key] = {
          name: `${entry.jurisdiction.name} ${entry.tax.name}`,
          type: `${entry.tax.type}`,
          amount: `${entry.tax.rate.formatted_decimal} ${entry.tax.calculation_type === 'per-reservation' ? '' : entry.tax.calculation_type
            }`,
          appliesTo: [entry.tax_subject.name],
        };
      } else {
        if (!groupedData[key].appliesTo.includes(entry.tax_subject.name)) {
          groupedData[key].appliesTo.push(entry.tax_subject.name);
        }
      }
    });

    return Object.values(groupedData)
      .map((entry: any) => {
        entry.appliesTo = entry.appliesTo.sort(this.sortAppliesTo);
        return entry;
      })
      .sort((a: any, b: any) => a.name.localeCompare(b.name));
  }

  sortAppliesTo(a: string, b: string): number {
    if (a === 'Nightly Rate') return -1;
    if (b === 'Nightly Rate') return 1;

    if (a === 'Cleaning Fee') return -1;
    if (b === 'Cleaning Fee') return 1;

    return a.localeCompare(b);
  }
}
